<template>
  <CalendarBase
    class="calendarDateTable"
    :calendar="calendar"
    @select-date="onSelectDateInput"
  >
    <template #default="{ date }">
      <div
        class="calendarDateTable__inner"
        :class="{ 'calendarDateTable__inner--desable': isDisabled(date) }"
        @mouseenter="onMouseEnterDate(date)"
      >
        <div class="calendarDateTable__date" :class="addDateClass(date)">
          {{ date.getDate() }}
        </div>
      </div>
    </template>
  </CalendarBase>
</template>

<script lang="ts">
import { Component, Vue, Prop } from "vue-property-decorator";
import { Calendar } from "@/models/date-picker/Calendar";
import { DateRange } from "@/components/date-picker/DateRange";
import { isSameDate } from "@/util/date-util";
import CalendarBase from "@/components/date-picker/calendar/CalendarBase.vue";
import { getCurrentDate } from "@/util/date-util";

@Component({
  components: { CalendarBase }
})
export default class CalendarDateTable extends Vue {
  @Prop({ type: Calendar, required: true })
  calendar!: Calendar;

  @Prop({ type: Date, default: null })
  selectedDate!: Date;

  // enabledPeriodで指定した期間のみ有効にする
  // null時は全期間有効
  @Prop({ type: Object, default: null })
  enabledPeriod!: DateRange;

  onSelectDate(date: Date) {
    this.$emit("select-date", date);
  }

  onMouseEnterDate(date: Date) {
    this.$emit("mouse-enter-date", date);
  }

  get currentMonthIndex(): number {
    return this.calendar.monthIndex;
  }

  onSelectDateInput(date: Date) {
    if (!this.isDisabled(date)) {
      this.onSelectDate(date);
    }
  }

  addDateClass(date: Date): string[] {
    const addClass: string[] = [];

    if (date.getMonth() !== this.currentMonthIndex) {
      return ["calendarDateTable__date--anotherMonth"];
    }

    // 今日の日付に付与
    const today = getCurrentDate();
    if (isSameDate(today, date)) {
      addClass.push("calendarDateTable__date--today");
    }

    // prop dates の日付に付与
    if (this.isSelectedDate(date)) {
      addClass.push("calendarDateTable__date--selected");
    }

    return addClass;
  }

  isSelectedDate(date: Date): boolean {
    if (this.selectedDate instanceof Date) {
      return isSameDate(this.selectedDate, date);
    }

    return false;
  }

  isDisabled(date: Date): boolean {
    if (this.enabledPeriod !== null) {
      const disabledMinDate = this.enabledPeriod.min;
      disabledMinDate.setHours(0, 0, 0, 0);

      const disabledMaxDate = this.enabledPeriod.max;
      disabledMaxDate.setHours(0, 0, 0, 0);

      return (
        date.getTime() < disabledMinDate.getTime() ||
        date.getTime() > disabledMaxDate.getTime()
      );
    }

    return false;
  }
}
</script>

<style scoped lang="scss">
.calendarDateTable__inner {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: $datePickerCellHeight;

  &:not(.calendarDateTable__inner--desable):hover {
    color: $colorElmentUIBlue900;
    cursor: pointer;
  }
}

.calendarDateTable__inner--desable {
  background-color: $colorElmentUIGray100;
  color: $colorElmentUIGray300;
  pointer-events: none;
}

.calendarDateTable__date {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 2px solid $colorClear;
  border-radius: 50%;
}

.calendarDateTable__date--today {
  color: $colorElmentUIBlue900;
  font-weight: bold;
}

.calendarDateTable__date--selected {
  background-color: $colorElmentUIBlue900;
  color: $colorWhite;
  cursor: pointer;
}

.calendarDateTable__date--anotherMonth {
  color: $colorElmentUIGray300;
}
</style>
